import React, { useState } from 'react';
import { X, Users, Briefcase } from 'lucide-react';
import { FunnelStageData, OfficerData, SourceBreakdownData } from '../types';
import { mockLeads, mockOpportunities } from '../mockData';
export type DrillContext = {
  type: 'stage';
  stage: FunnelStageData;
} | {
  type: 'officer';
  officer: OfficerData;
} | {
  type: 'source';
  source: SourceBreakdownData;
  totalFundedAcrossSources: number;
};
interface DrillDownModalProps {
  context: DrillContext;
  onClose: () => void;
}
// Synthetic borrower name pool for source drill-down
const borrowerNames = ['Erica Robinson', 'Christopher Cotter', 'Cole Mwendafilumba', 'Jesse Meers', 'Michael Harmon', 'Fred Keith', 'Jordan Goodin', 'Tasha Reynolds', 'Marcus Lee', 'Priya Patel', 'Dean Whitaker', 'Sofia Alvarez', 'Brandon Ng', 'Heather Voss', 'Omar Haddad', 'Lisa Tran', 'Antwan Pierce', 'Kelly Stein', 'Reggie Booker', "Megan O'Brien", 'Travis Hwang', 'Naomi Carter', 'Vince DeLuca', 'Sasha Mendoza'];
const stageOptions = ['Funded', 'CTC', 'AWC', 'Sub. UW', 'Disc. Received', 'Disc. Sent'];
function generateSourceDeals(source: SourceBreakdownData) {
  // Deterministic seed from source id
  const seed = source.id.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const rand = (i: number) => {
    const x = Math.sin(seed * 13 + i * 7) * 10000;
    return x - Math.floor(x);
  };
  const count = source.funded;
  const avg = source.avgAmount || 250000;
  const deals = [];
  for (let i = 0; i < count; i++) {
    const name = borrowerNames[(seed + i * 3) % borrowerNames.length];
    const variance = 0.65 + rand(i) * 0.7; // 0.65x to 1.35x of avg
    const amount = Math.round(avg * variance / 1000) * 1000;
    const loanNum = 1526160000 + Math.floor(rand(i + 100) * 999999);
    const day = 1 + Math.floor(rand(i + 200) * 28);
    const fundedDate = `Apr ${day.toString().padStart(2, '0')}, 2026`;
    const convDay = Math.max(1, day - 14 - Math.floor(rand(i + 300) * 10));
    const convertedDate = `${convDay > day ? 'Mar' : 'Apr'} ${(convDay > day ? 31 - (convDay - day) : convDay).toString().padStart(2, '0')}, 2026`;
    deals.push({
      id: `${source.id}-d${i}`,
      name: `${name} - ${loanNum}`,
      stage: 'Funded',
      amount,
      converted: convertedDate,
      funded: fundedDate
    });
  }
  return deals;
}
function generateSourceLeads(source: SourceBreakdownData) {
  const seed = source.id.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const rand = (i: number) => {
    const x = Math.sin(seed * 17 + i * 11) * 10000;
    return x - Math.floor(x);
  };
  // Show a reasonable sample, not the full thousands
  const sampleSize = Math.min(50, source.created);
  const leads = [];
  for (let i = 0; i < sampleSize; i++) {
    const name = borrowerNames[(seed + i * 5) % borrowerNames.length];
    const day = 1 + Math.floor(rand(i) * 28);
    const stage = stageOptions[Math.floor(rand(i + 500) * stageOptions.length)];
    leads.push({
      id: `${source.id}-l${i}`,
      name,
      source: source.source,
      created: `Apr ${day.toString().padStart(2, '0')}, 2026`,
      status: stage
    });
  }
  return leads;
}
const formatCurrency = (n: number) => {
  if (n === 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
export function DrillDownModal({
  context,
  onClose
}: DrillDownModalProps) {
  const isOfficer = context.type === 'officer';
  const isSource = context.type === 'source';
  const title = context.type === 'officer' ? `${context.officer.name} · Drill-down` : context.type === 'source' ? `${context.source.source} · Deals` : `${context.stage.name} Drill-down`;
  // Tab counts
  const leadsCount = isOfficer ? context.officer.created : isSource ? context.source.created : context.stage.count;
  const oppsCount = isOfficer ? context.officer.funded : isSource ? context.source.funded : context.stage.count;
  const [activeTab, setActiveTab] = useState<'leads' | 'opportunities'>(isSource ? 'opportunities' : isOfficer ? 'leads' : context.type === 'stage' && (context.stage.id === 'created' || context.stage.id === 'converted') ? 'leads' : 'opportunities');
  // Build data sets
  const sourceDeals = isSource ? generateSourceDeals(context.source) : [];
  const sourceLeads = isSource ? generateSourceLeads(context.source) : [];
  const leadsList = isSource ? sourceLeads : mockLeads;
  const oppsList = isSource ? sourceDeals : mockOpportunities;
  const footerCount = activeTab === 'leads' ? leadsList.length : oppsList.length;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3d3d3d]/60 backdrop-blur-sm p-4" data-id="element-13">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200" data-id="element-14">
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-[#3d3d3d] text-white" data-id="element-15">
          <h2 className="text-xl font-bold flex items-center gap-3" data-id="element-16">
            <div className="w-2 h-6 rounded-sm" style={{
            backgroundColor: isSource ? context.source.color : '#c9a84c'
          }} data-id="element-17"></div>
            {title}
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-colors" data-id="element-18">
            <X size={20} strokeWidth={2.5} data-id="element-19" />
          </button>
        </div>

        {/* Officer summary strip */}
        {isOfficer && <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 grid grid-cols-2 md:grid-cols-6 gap-3 text-center" data-id="element-20">
            <div data-id="element-21">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-22">
                Created
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-23">
                {context.officer.created}
              </div>
            </div>
            <div data-id="element-24">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-25">
                Converted
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-26">
                {context.officer.converted}
              </div>
            </div>
            <div data-id="element-27">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-28">
                Disc Sent
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-29">
                {context.officer.discSent}
              </div>
            </div>
            <div data-id="element-30">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-31">
                Sub UW
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-32">
                {context.officer.subUw}
              </div>
            </div>
            <div data-id="element-33">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-34">
                Funded
              </div>
              <div className="text-lg font-bold text-[#c9a84c]" data-id="element-35">
                {context.officer.funded}
              </div>
            </div>
            <div data-id="element-36">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-37">
                Pull %
              </div>
              <div className="text-lg font-bold text-[#5a7fa5]" data-id="element-38">
                {context.officer.pullThrough}%
              </div>
            </div>
          </div>}

        {/* Source summary strip */}
        {isSource && <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 grid grid-cols-2 md:grid-cols-6 gap-3 text-center" data-id="element-39">
            <div data-id="element-40">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-41">
                Created
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-42">
                {context.source.created.toLocaleString()}
              </div>
            </div>
            <div data-id="element-43">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-44">
                Converted
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-45">
                {context.source.converted.toLocaleString()}
              </div>
            </div>
            <div data-id="element-46">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-47">
                Funded
              </div>
              <div className="text-lg font-bold text-[#c9a84c]" data-id="element-48">
                {context.source.funded}
              </div>
            </div>
            <div data-id="element-49">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-50">
                Volume
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-51">
                {formatCurrency(context.source.fundedAmount)}
              </div>
            </div>
            <div data-id="element-52">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-53">
                Avg Loan
              </div>
              <div className="text-lg font-bold text-[#3d3d3d]" data-id="element-54">
                {formatCurrency(context.source.avgAmount)}
              </div>
            </div>
            <div data-id="element-55">
              <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider" data-id="element-56">
                Pull %
              </div>
              <div className="text-lg font-bold text-[#5a7fa5]" data-id="element-57">
                {context.source.pullThrough}%
              </div>
            </div>
          </div>}

        {/* Tabs */}
        <div className="px-6 pt-6 pb-4 flex gap-4 border-b border-gray-100" data-id="element-58">
          <button onClick={() => setActiveTab('leads')} className={`flex items-center gap-2 px-5 py-2.5 rounded-t-lg border-b-2 text-sm font-bold transition-colors ${activeTab === 'leads' ? 'border-[#c9a84c] text-[#3d3d3d] bg-[#c9a84c]/5' : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`} data-id="element-59">
            <Users size={18} data-id="element-60" />
            Leads
            <span className={`px-2 py-0.5 rounded-md text-xs ml-1 ${activeTab === 'leads' ? 'bg-[#c9a84c] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-61">
              {leadsCount.toLocaleString()}
            </span>
          </button>
          <button onClick={() => setActiveTab('opportunities')} className={`flex items-center gap-2 px-5 py-2.5 rounded-t-lg border-b-2 text-sm font-bold transition-colors ${activeTab === 'opportunities' ? 'border-[#c9a84c] text-[#3d3d3d] bg-[#c9a84c]/5' : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`} data-id="element-62">
            <Briefcase size={18} data-id="element-63" />
            {isSource ? 'Funded Deals' : 'Opportunities'}
            <span className={`px-2 py-0.5 rounded-md text-xs ml-1 ${activeTab === 'opportunities' ? 'bg-[#c9a84c] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-64">
              {oppsCount.toLocaleString()}
            </span>
          </button>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-auto px-6 pb-2 bg-gray-50/30" data-id="element-65">
          {activeTab === 'leads' ? <table className="w-full text-sm text-left" data-id="element-66">
              <thead className="text-xs text-gray-500 uppercase border-b border-gray-200 sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10" data-id="element-67">
                <tr data-id="element-68">
                  <th className="py-4 font-bold tracking-wider w-1/3" data-id="element-69">NAME</th>
                  <th className="py-4 font-bold tracking-wider w-1/4" data-id="element-70">
                    SOURCE
                  </th>
                  <th className="py-4 font-bold tracking-wider w-1/4" data-id="element-71">
                    CREATED
                  </th>
                  <th className="py-4 font-bold tracking-wider" data-id="element-72">STATUS</th>
                </tr>
              </thead>
              <tbody data-id="element-73">
                {leadsList.map(lead => <tr key={lead.id} className="border-b border-gray-100 hover:bg-white transition-colors" data-id="element-74">
                    <td className="py-4 font-semibold text-[#5a7fa5] hover:text-[#4a6f95] hover:underline cursor-pointer" data-id="element-75">
                      {lead.name}
                    </td>
                    <td className="py-4 font-medium text-gray-700" data-id="element-76">
                      {lead.source}
                    </td>
                    <td className="py-4 font-medium text-gray-700" data-id="element-77">
                      {lead.created}
                    </td>
                    <td className="py-4" data-id="element-78">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200" data-id="element-79">
                        {lead.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table> : <table className="w-full text-sm text-left" data-id="element-80">
              <thead className="text-xs text-gray-500 uppercase border-b border-gray-200 sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10" data-id="element-81">
                <tr data-id="element-82">
                  <th className="py-4 font-bold tracking-wider w-1/3" data-id="element-83">NAME</th>
                  <th className="py-4 font-bold tracking-wider" data-id="element-84">STAGE</th>
                  <th className="py-4 font-bold tracking-wider" data-id="element-85">AMOUNT</th>
                  <th className="py-4 font-bold tracking-wider" data-id="element-86">CONVERTED</th>
                  <th className="py-4 font-bold tracking-wider" data-id="element-87">FUNDED</th>
                </tr>
              </thead>
              <tbody data-id="element-88">
                {oppsList.length === 0 ? <tr data-id="element-89">
                    <td colSpan={5} className="py-10 text-center text-sm text-gray-400" data-id="element-90">
                      No funded deals for this source yet.
                    </td>
                  </tr> : oppsList.map(opp => <tr key={opp.id} className="border-b border-gray-100 hover:bg-white transition-colors" data-id="element-91">
                      <td className="py-4 font-semibold text-[#5a7fa5] hover:text-[#4a6f95] hover:underline cursor-pointer" data-id="element-92">
                        {opp.name}
                      </td>
                      <td className="py-4" data-id="element-93">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-[#c9a84c]/10 text-[#c9a84c] border border-[#c9a84c]/20" data-id="element-94">
                          {opp.stage}
                        </span>
                      </td>
                      <td className="py-4 font-bold text-[#3d3d3d] text-base" data-id="element-95">
                        ${opp.amount.toLocaleString()}
                      </td>
                      <td className="py-4 font-medium text-gray-600" data-id="element-96">
                        {opp.converted}
                      </td>
                      <td className="py-4 font-bold text-[#c9a84c]" data-id="element-97">
                        {opp.funded}
                      </td>
                    </tr>)}
              </tbody>
            </table>}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 text-xs font-medium text-gray-500 flex justify-between items-center" data-id="element-98">
          <span data-id="element-99">
            Showing{' '}
            <strong className="text-[#3d3d3d]" data-id="element-100">
              {footerCount.toLocaleString()}
            </strong>{' '}
            records
          </span>
        </div>
      </div>
    </div>;
}